import { createAsyncThunk } from '@reduxjs/toolkit';
import { defineMessages } from 'react-intl';

import {
  dropzoneEnqueueUploadingFile,
  dropzoneRemoveUploadedFiles,
  dropzoneUpdateUploadingProgress,
} from '~/actions/dropzone';
import {
  uploadDocuments,
  type UploadDocumentFile,
  type UploadDocumentOptions,
  type UploadFileApiConfig,
} from '~/api/document';
import { getUploadLogs } from '~/api/users';
import { showToast } from '~/eds/src/components/Toast/showToast';
import { UploadStatusType } from '~/enums/UploadStatusType';
import { intlService } from '~/i18n/IntlService';

import { slices } from '../slices';

interface UploadDocumentsArgs {
  files: UploadDocumentFile[];
  apiConfig: UploadFileApiConfig;
  uploadFormFields: unknown;
}

const messages = defineMessages({
  uploadFailed: {
    id: 'FileUpload.uploadFailed',
    defaultMessage:
      'The upload did not complete. Try uploading the file again.',
    description:
      'Fallback error detail when a single upload fails without a server message',
  },
  serviceError: {
    id: 'FileUpload.serviceError',
    defaultMessage:
      'The service is unavailable. Try uploading the file again later.',
    description:
      'Error toast shown when the upload service returns an HTML error page',
  },
  uploadFailedBatch: {
    id: 'FileUpload.uploadFailedBatch',
    defaultMessage:
      '{count, plural, one {# document could not be submitted.} other {# documents could not be submitted.}}',
    description:
      'Error toast summarizing how many documents failed the upload request. Example: count = 3',
  },
  uploadSucceeded: {
    id: 'FileUpload.uploadSucceeded',
    defaultMessage:
      '{count, plural, one {# document submitted for processing.} other {# documents submitted for processing.}}',
    description:
      'Success toast summarizing how many documents reached the server. Processing happens afterwards, so the count is not a count of finished documents. Example: count = 12',
  },
});

const getServerDetail = (error: unknown) => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;

  return typeof detail === 'string' ? detail : undefined;
};

/**
 * Failures with no HTTP response (timeouts, dropped connections) carry no
 * server `detail`, so fall back through the error's own fields. Every branch
 * has to end at a string: the toast renders it, and anything thrown here
 * escapes the upload's catch block and takes the rest of the batch with it.
 */
const getErrorDetail = (error: unknown) => {
  if (typeof error === 'string') {
    return error;
  }

  const legacyDetail = (error as { detail?: unknown })?.detail;
  if (typeof legacyDetail === 'string') {
    return legacyDetail;
  }

  const message = (error as { message?: unknown })?.message;
  if (typeof message === 'string') {
    return message;
  }

  return intlService.getIntl().formatMessage(messages.uploadFailed);
};

/*************
 * Thunks
 *************/
export const getUserUploads = createAsyncThunk(
  'fileUpload/getUserUploads',
  () => getUploadLogs(),
);

export const openUploadInfoPanelAndGetLogs = createAsyncThunk(
  'fileUpload/openUploadInfoPanelAndGetLogs',
  async (__unused__: void, thunkAPI) => {
    // open before fetching not after, in case the user closes before the get request resolves
    thunkAPI.dispatch(slices.deprecatedUpload.actions.openUploadInfoPanel());

    await thunkAPI.dispatch(getUserUploads());
  },
);

export const uploadDocumentsThunk = createAsyncThunk(
  'fileUpload/uploadDocuments',
  async (
    { files, apiConfig, uploadFormFields }: UploadDocumentsArgs,
    thunkAPI,
  ) => {
    let uploadedCount = 0;
    let failedCount = 0;
    let lastFailureDetail: string | undefined;
    const hasUploadLogs = ({ service, url }: UploadFileApiConfig) =>
      service === 'pilot' && url === '/document/upload/';

    const onUploadStart: UploadDocumentOptions['onUploadStart'] = (file) => {
      const processingFile = {
        id: file.upload.uuid,
        document_name: file.name,
        size: file.size,
        date_added: new Date(),
        progress: 0,
        processing_status: UploadStatusType.Uploading,
      };

      thunkAPI.dispatch(dropzoneEnqueueUploadingFile(processingFile));
    };

    const onUploadProgress: UploadDocumentOptions['onUploadProgress'] = (
      file,
      event,
    ) => {
      const { total, loaded } = event;
      const progress = total ? (loaded / total) * 100 : 0;
      const bytesSent = loaded;
      if (progress === 100 && file.size - bytesSent > 0) return;

      thunkAPI.dispatch(
        dropzoneUpdateUploadingProgress({ id: file.upload.uuid, progress }),
      );
    };

    const onUploadError: UploadDocumentOptions['onUploadError'] = (
      file,
      error,
    ) => {
      const serverDetail = getServerDetail(error);
      let detail = serverDetail ?? getErrorDetail(error);
      const processingStatus = serverDetail?.includes('File type not supported')
        ? UploadStatusType.UploadFileTypeFailure
        : UploadStatusType.UploadFailed;

      if (detail.includes('DOCTYPE')) {
        detail = intlService.getIntl().formatMessage(messages.serviceError);
      }

      failedCount += 1;
      lastFailureDetail = detail;

      thunkAPI.dispatch(
        dropzoneUpdateUploadingProgress({
          id: file.upload.uuid,
          processing_status: processingStatus,
        }),
      );
    };

    const onUploadComplete: UploadDocumentOptions['onUploadComplete'] = (
      file,
    ) => {
      uploadedCount += 1;

      thunkAPI.dispatch(dropzoneRemoveUploadedFiles([file.upload.uuid]));
    };

    const options: UploadDocumentOptions = {
      apiConfig,
      onUploadStart,
      onUploadProgress,
      onUploadError,
      onUploadComplete,
    };

    files.forEach((file) => {
      file.upload_form_data = uploadFormFields;
    });

    await uploadDocuments(files, options);

    // One toast per outcome for the whole batch. Per-file toasts bury each
    // other on large uploads. Counts are request outcomes only — processing
    // after a successful POST still lives in the upload log.
    if (failedCount > 0) {
      const intl = intlService.getIntl();
      const message =
        failedCount === 1 && lastFailureDetail
          ? lastFailureDetail
          : intl.formatMessage(messages.uploadFailedBatch, {
              count: failedCount,
            });

      showToast({ status: 'danger', message, autoDismiss: false });
    }

    if (uploadedCount > 0) {
      showToast({
        status: 'success',
        message: intlService
          .getIntl()
          .formatMessage(messages.uploadSucceeded, { count: uploadedCount }),
      });
    }

    if (hasUploadLogs(apiConfig)) {
      thunkAPI.dispatch(openUploadInfoPanelAndGetLogs());
    }
  },
);
